import React, { useMemo, useState } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useHistory } from "react-router-dom";
import { BUILDER_GUIDE } from "@/config";
import { Icon } from "@/components";
import { Button } from "@/ui";
import { useStoreContext } from "context";
import menuStyles from "../../../ui/components/ButtonDropdown/ButtonDropdown.module.scss";

import styles from "./ToolBar.module.scss";

export type ToolBarRoute = {
    guide?: boolean;
    root?: boolean;
    route?: string;
};

export type ToolBarProps = {
    route?: ToolBarRoute;
};

const ToolBar = ({ route }: ToolBarProps) => {
    const history = useHistory();
    const { externalUrls, externalLoading, loading } = useStoreContext();
    const [menuOpen, setMenuOpen] = useState(false);

    const { prismicURL, storeURL } = useMemo(
        () => ({
            prismicURL: externalUrls?.prismicURL ?? null,
            storeURL: externalUrls?.storeURL ?? null,
        }),
        [externalUrls],
    );

    const isLoading = Boolean(externalLoading || loading);
    const isPrismicDisabled = isLoading || !prismicURL;
    const isStoreDisabled = isLoading || !storeURL;
    const showGoBack = !route?.root && route?.route !== "/settings";

    const goToPrismic = () => {
        if (prismicURL) {
            window.open(prismicURL, "_blank", "noopener,noreferrer");
        }
    };

    const goToStore = () => {
        if (storeURL) {
            window.open(storeURL, "_blank", "noopener,noreferrer");
        }
    };

    const goToDemo = () => {
        window.open(BUILDER_GUIDE?.demoUrl, "_blank", "noopener,noreferrer");
    };

    const goBack = () => {
        history.goBack();
    };

    const trailingIcon = isLoading ? (
        <span aria-hidden className={styles.inlineSpinner} />
    ) : (
        <Icon icon="external-link" />
    );

    const menuItems = [
        {
            id: "launch-cms",
            label: "Launch CMS",
            icon: <Icon icon="document" />,
            disabled: isPrismicDisabled,
            onSelect: goToPrismic,
        },
        {
            id: "launch-ecomm",
            label: "Launch Ecomm Site",
            icon: <Icon icon="store-ecomm" />,
            disabled: isStoreDisabled,
            onSelect: goToStore,
        },
        ...(route?.guide
            ? [
                  {
                      id: "launch-demo",
                      label: "Launch Demo Site",
                      icon: <Icon icon="show" />,
                      disabled: false,
                      onSelect: goToDemo,
                  },
              ]
            : []),
        ...(showGoBack
            ? [
                  {
                      id: "go-back",
                      label: "Go back",
                      icon: <i aria-hidden className="fas fa-reply" />,
                      disabled: false,
                      onSelect: goBack,
                  },
              ]
            : []),
    ];

    const allDisabled = menuItems.every(item => item.disabled);

    return (
        <div className={styles.root}>
            <div className={styles.desktop} role="group">
                <Button
                    disabled={isPrismicDisabled}
                    iconLeft={<Icon icon="document" />}
                    iconRight={trailingIcon}
                    onClick={goToPrismic}
                    size="sm"
                    type="button"
                    variant={route?.guide ? "primary" : "secondary"}
                >
                    Launch CMS
                </Button>
                <Button
                    disabled={isStoreDisabled}
                    iconLeft={<Icon icon="store-ecomm" />}
                    iconRight={trailingIcon}
                    onClick={goToStore}
                    size="sm"
                    type="button"
                    variant="secondary"
                >
                    Launch Ecomm Site
                </Button>
                {route?.guide ? (
                    <Button
                        iconLeft={<Icon icon="show" />}
                        iconRight={<Icon icon="external-link" />}
                        onClick={goToDemo}
                        size="sm"
                        type="button"
                        variant="secondary"
                    >
                        Launch Demo Site
                    </Button>
                ) : null}
                {showGoBack ? (
                    <Button
                        iconLeft={<i aria-hidden className="fas fa-reply" />}
                        onClick={goBack}
                        size="sm"
                        type="button"
                        variant="tertiary"
                    >
                        Go back
                    </Button>
                ) : null}
            </div>

            <div className={styles.mobile}>
                <DropdownMenu.Root onOpenChange={setMenuOpen} open={menuOpen}>
                    <DropdownMenu.Trigger asChild>
                        <Button
                            aria-label="Go to"
                            disabled={allDisabled}
                            iconRight={<Icon icon="external-link" />}
                            size="sm"
                            type="button"
                            variant="secondary"
                        >
                            Go to
                        </Button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Portal>
                        <DropdownMenu.Content
                            align="end"
                            className={`${menuStyles.content} ${styles.menu}`}
                            loop
                            side="bottom"
                            sideOffset={6}
                        >
                            {menuItems.map(item => (
                                <DropdownMenu.Item
                                    className={menuStyles.item}
                                    disabled={item.disabled}
                                    key={item.id}
                                    onSelect={() => item.onSelect()}
                                >
                                    <span
                                        aria-hidden
                                        className={styles.menuIcon}
                                    >
                                        {item.icon}
                                    </span>
                                    <span className={styles.menuLabel}>
                                        {item.label}
                                    </span>
                                </DropdownMenu.Item>
                            ))}
                        </DropdownMenu.Content>
                    </DropdownMenu.Portal>
                </DropdownMenu.Root>
            </div>
        </div>
    );
};

export default ToolBar;
